import React, { useState, useContext, useEffect } from 'react';
import Header from '../../components/Header/Header';
import useTranslation from '../../hooks/useTranslation';
import { TranslationContext } from '../../helpers/Context';
import { useAnalytics } from '../../hooks/useAnalytics';
import './feedback.css';

const Feedback = () => {
  const T = useTranslation();
  const { language } = useContext(TranslationContext);
  const { 
    trackFeedbackStarted, 
    trackFeedbackSubmitted, 
    trackFeedbackRating, 
    trackFeedbackRecommendation, 
    trackFeedbackComments, 
    trackFeedbackError 
  } = useAnalytics();
  
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Estado para las calificaciones
  const [ratings, setRatings] = useState({
    intuitive: 0,
    agility: 0,
    usefulness: 0
  });

  // Estado para la recomendación
  const [recommendation, setRecommendation] = useState(null);

  // Estado para los comentarios
  const [comments, setComments] = useState('');

  // Generar un ID de sesión único para este feedback
  const sessionId = React.useMemo(() => {
    return `feedback_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Track cuando el usuario accede a la página de feedback
  useEffect(() => {
    trackFeedbackStarted({
      language,
      sessionId,
      page_url: window.location.href
    });
  }, [trackFeedbackStarted, language, sessionId]);

  // Datos de las calificaciones
  const ratingCategories = [
    {
      key: 'intuitive',
      label: 'feedback_intuitive',
      icon: '🧠'
    },
    {
      key: 'agility',
      label: 'feedback_agility',
      icon: '⚡'
    },
    {
      key: 'usefulness',
      label: 'feedback_usefulness',
      icon: '🎯'
    }
  ];

  // Función para manejar las calificaciones con estrellas
  const handleRating = (category, value) => {
    setRatings(prev => ({
      ...prev,
      [category]: value
    }));
    setError('');
    
    // Track el rating individual
    trackFeedbackRating(category, value, {
      language,
      sessionId,
      previous_rating: ratings[category]
    });
  };

  // Función para renderizar las estrellas
  const renderStars = (category, currentRating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <div
          key={i}
          className={`feedback-star ${i <= currentRating ? 'filled' : ''}`}
          onClick={() => handleRating(category, i)}
          onMouseEnter={(e) => {
            if (i <= currentRating) return;
            e.target.classList.add('active');
          }}
          onMouseLeave={(e) => {
            if (i <= currentRating) return;
            e.target.classList.remove('active');
          }}
        >
          ★
        </div>
      );
    }
    return stars;
  };

  // Función para manejar el envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validación: al menos una calificación
    const hasRating = Object.values(ratings).some(rating => rating > 0);
    if (!hasRating) {
      setError(T('feedback_required'));
      trackFeedbackError('validation_error', 'No ratings provided', {
        language,
        sessionId
      });
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Preparar datos de feedback para Firebase Analytics
      const feedbackData = {
        intuitive: ratings.intuitive,
        agility: ratings.agility,
        usefulness: ratings.usefulness,
        recommendation: recommendation,
        comments: comments
      };

      // Enviar feedback a Firebase Analytics
      trackFeedbackSubmitted(feedbackData, {
        language,
        sessionId,
        page_url: window.location.href,
        user_agent: navigator.userAgent,
        screen_resolution: `${screen.width}x${screen.height}`,
        time_on_page: Date.now() - sessionId.split('_')[1], // Tiempo aproximado en la página
        total_ratings_given: Object.values(ratings).filter(r => r > 0).length,
        has_recommendation: recommendation !== null,
        has_comments: comments.trim().length > 0
      });

      // Simular delay para mejor UX
      await new Promise(resolve => setTimeout(resolve, 1000));

      setIsSubmitted(true);
    } catch (err) {
      console.error('Error sending feedback:', err);
      setError(T('feedback_error'));
      trackFeedbackError('submission_error', err.message, {
        language,
        sessionId,
        error_details: err.toString()
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Si ya se envió el feedback, mostrar mensaje de éxito
  if (isSubmitted) {
    return (
      <div>
        <Header />
        <div className="feedback-container">
          <div className="feedback-form">
            <div className="feedback-success">
              <div className="feedback-success-icon">✓</div>
              <h2 className="feedback-success-title">{T('feedback_success')}</h2>
              <p className="feedback-success-message">
                Tu opinión es muy valiosa para nosotros y nos ayudará a mejorar la aplicación.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <div className="feedback-container">
        <h1 className="feedback-title">{T('feedback_title')}</h1>
        <p className="feedback-subtitle">{T('feedback_subtitle')}</p>
        
        <form className="feedback-form" onSubmit={handleSubmit}>
          {/* Sección de Calificaciones */}
          <div className="feedback-section">
            <h3 className="feedback-section-title">
              <div className="feedback-section-icon">⭐</div>
              Calificaciones
            </h3>
            
            {ratingCategories.map((category) => (
              <div key={category.key} className="feedback-rating">
                <span className="feedback-rating-label">
                  <span style={{ marginRight: '8px' }}>{category.icon}</span>
                  {T(category.label)}
                </span>
                <div className="feedback-stars">
                  {renderStars(category.key, ratings[category.key])}
                </div>
              </div>
            ))}
          </div>

          {/* Sección de Recomendación */}
          <div className="feedback-section">
            <h3 className="feedback-section-title">
              <div className="feedback-section-icon">💬</div>
              Recomendación
            </h3>
            
            <div className="feedback-recommendation">
              <span className="feedback-rating-label">
                {T('feedback_recommendation')}
              </span>
              <div className="feedback-recommendation-buttons">
                <button
                  type="button"
                  className={`feedback-button ${recommendation === true ? 'active' : ''}`}
                  onClick={() => {
                    setRecommendation(true);
                    // Track la selección de recomendación
                    trackFeedbackRecommendation(true, {
                      language,
                      sessionId,
                      previous_recommendation: recommendation
                    });
                  }}
                >
                  {T('feedback_yes')}
                </button>
                <button
                  type="button"
                  className={`feedback-button ${recommendation === false ? 'active' : ''}`}
                  onClick={() => {
                    setRecommendation(false);
                    // Track la selección de recomendación
                    trackFeedbackRecommendation(false, {
                      language,
                      sessionId,
                      previous_recommendation: recommendation
                    });
                  }}
                >
                  {T('feedback_no')}
                </button>
              </div>
            </div>
          </div>

          {/* Sección de Comentarios */}
          <div className="feedback-section">
            <h3 className="feedback-section-title">
              <div className="feedback-section-icon">📝</div>
              {T('feedback_comments')}
            </h3>
            
            <div className="feedback-comments">
              <textarea
                className="feedback-textarea"
                placeholder={T('feedback_comments_placeholder')}
                value={comments}
                onChange={(e) => {
                  const newComments = e.target.value;
                  setComments(newComments);
                  
                  // Track cuando el usuario escribe comentarios (solo metadata)
                  if (newComments.length > 0 && newComments.length % 50 === 0) {
                    trackFeedbackComments(true, newComments.length, {
                      language,
                      sessionId,
                      is_typing: true
                    });
                  }
                }}
                onBlur={() => {
                  // Track cuando el usuario termina de escribir comentarios
                  if (comments.length > 0) {
                    trackFeedbackComments(true, comments.length, {
                      language,
                      sessionId,
                      is_typing: false
                    });
                  }
                }}
                rows={4}
              />
            </div>
          </div>

          {/* Mensaje de error */}
          {error && (
            <div className="feedback-error">
              {error}
            </div>
          )}

          {/* Botón de envío */}
          <button
            type="submit"
            className="feedback-submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? T('feedback_sending') : T('feedback_send')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Feedback; 